
import { useCallback } from "react";
import { useFolderContent } from "./folderContent";
import { useDeleteAssessment } from "./useDeleteAssessment";
import type { Assessment } from "../types.d";

interface UseFolderViewModelProps {
  projectId: string;
  folderId: string;
  onNavigateToFolder: (path?: string) => void;
  onStartAssessment?: (projectId: string, folderId: string) => void;
  onNavigateToAssessment: (assessmentId: string, folderId: string) => void;
  isSharedFolder?: boolean;
  onFolderUpdated?: () => void;
}

export const useFolderViewModel = ({
  projectId,
  folderId,
  onNavigateToFolder,
  onStartAssessment,
  onNavigateToAssessment,
  isSharedFolder = false,
  onFolderUpdated
}: UseFolderViewModelProps) => {
  const {
    folder: folderData,
    subfolders,
    assessments,
    isLoading,
    error,
    refetch
  } = useFolderContent({ 
    folderId, 
    projectId
  });

  // Set up the delete assessment hook with proper callback
  const { isLoading: isDeleting, handleDeleteAssessment: performDelete } = useDeleteAssessment(
    (assessmentId: string) => {
      console.log("Assessment deleted, refreshing data...");
      refetch(); // Refresh the folder content after successful deletion
    }
  );

  const handleOpenFolder = useCallback((newFolderId: string) => {
    const effectiveProjectId = folderData?.project_id || projectId;
    onNavigateToFolder(`/uppdrag/${effectiveProjectId}/${newFolderId}`);
  }, [onNavigateToFolder, projectId, folderData]);

  const handleStartAssessment = useCallback((providedProjectId: string, providedFolderId: string) => {
    // Use the most reliable project ID: folder data, provided, or prop
    const effectiveProjectId = folderData?.project_id || providedProjectId || projectId;
    const effectiveFolderId = providedFolderId || folderId;
    
    console.log("useFolderViewModel - handleStartAssessment called:", {
      providedProjectId,
      providedFolderId,
      effectiveProjectId,
      effectiveFolderId,
      folderDataProjectId: folderData?.project_id
    });
    
    if (!effectiveProjectId || !effectiveFolderId) {
      console.error("Missing required IDs for assessment:", {
        effectiveProjectId,
        effectiveFolderId
      });
      return;
    }
    
    if (onStartAssessment) {
      onStartAssessment(effectiveProjectId, effectiveFolderId);
    }
  }, [onStartAssessment, projectId, folderId, folderData]);

  const handleNavigateToAssessment = useCallback((assessmentId: string, providedFolderId: string) => {
    const effectiveFolderId = providedFolderId || folderId;
    console.log("Navigating to existing assessment:", { assessmentId, effectiveFolderId });
    onNavigateToAssessment(assessmentId, effectiveFolderId);
  }, [onNavigateToAssessment, folderId]);

  const handleCreateFolder = useCallback(async (name: string, description?: string) => {
    console.log("Creating folder:", { name, description, folderId });
    if (onFolderUpdated) {
      onFolderUpdated();
    }
  }, [folderId, onFolderUpdated]);

  const handleDeleteFolder = useCallback(async (folderId: string) => {
    console.log("Deleting folder:", folderId);
    if (onFolderUpdated) {
      onFolderUpdated();
    }
  }, [onFolderUpdated]);

  const handleDeleteAssessment = useCallback(async (assessmentId: string) => {
    console.log("Deleting assessment:", assessmentId);
    await performDelete(assessmentId);
  }, [performDelete]);

  return {
    folderData,
    subfolders,
    assessments,
    isLoading: isLoading || isDeleting,
    error,
    refetch,
    isShared: isSharedFolder,
    handleOpenFolder,
    handleStartAssessment,
    handleNavigateToAssessment,
    handleCreateFolder,
    handleDeleteFolder,
    handleDeleteAssessment,
    refreshData: refetch
  };
};
